import { FarmRiskType, lookupsJson } from '@ourbranch/lookups';
import { buildDefaultFarmLiabilityCoverage as buildCarrierFarmLiabilityCoverage } from '@ourbranch/farm-utils';
import { brandName } from 'brand-config';

import { appConfig } from 'brand-config';

// #33672 / #33674: farm liability limit dropdowns can be restricted/overridden per brand via
// appConfig.farm[state].liabilityCoverageOptions.
//   - Medical (M): brands may restrict the shared lookup (CFM offers ONLY $10,000). Falls back to the
//     full @ourbranch/lookups list when the brand doesn't override it.
//   - Limited Farm Pollution: there is no shared lookup — a brand opts into an editable dropdown by
//     providing options (CFM: $25,000 default + $50,000 / $100,000). Returns null otherwise, so the
//     field stays a read-only display for brands that don't offer selectable pollution limits.
export const getMedicalExpenseLimitOptions = (state) =>
  appConfig?.farm?.[state]?.liabilityCoverageOptions?.medicalExpensePerPerson ??
  lookupsJson.farm.medicalExpensePerPersonLimit;

export const getFarmPollutionLimitOptions = (state) =>
  appConfig?.farm?.[state]?.liabilityCoverageOptions?.farmPollution ?? null;

// Issue #30825: liability factories live in @ourbranch/farm-utils so the
// staff-fe and forreston post-data-rules share one source of truth for the
// empty / default shape and can't drift as FarmLiabilityCoverageDetails grows.
// `buildEmptyFarmLiabilityCoverage` is applied whenever the Liability toggle
// flips (ON or OFF); `buildDefaultFarmLiabilityCoverage` repopulates Coverage
// L / M / aggregate when the user selects or switches the liability type — NOT
// on toggle ON (#31288 reverted the #30825 follow-up that pre-applied defaults
// on toggle ON). The default-builder is carrier-keyed (#33168) — bound to the
// build-time brand here so CFM type switches fill CFM limits, not Forreston's.
export { buildEmptyFarmLiabilityCoverage } from '@ourbranch/farm-utils';
export const buildDefaultFarmLiabilityCoverage = (type) => buildCarrierFarmLiabilityCoverage(type, brandName);

const RESIDENCE_RISK_TYPES = [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4];

const RESIDENCE_FORM_LABELS = {
  [FarmRiskType.Fo1]: 'FO-1 Basic form',
  [FarmRiskType.Fo2]: 'FO-2 Broad form',
  [FarmRiskType.Fo3]: 'FO-3 Special form',
  [FarmRiskType.Fo4]: 'FO-4 Renters risk'
};

const getStructureLabel = (risk, state) => {
  const structureCategories = lookupsJson.farm.structureCategoriesByState?.[state] || [];
  const buildingTypeLabel =
    structureCategories.find((c) => c.id === risk.building?.buildingType)?.value || risk.building?.buildingType || '';
  return `FO-6 Structures risk: ${buildingTypeLabel} - ${risk.description}`;
};

const getResidenceLabel = (risk) => {
  const propertyTypes = lookupsJson.farm.propertyType || [];
  const formLabel = RESIDENCE_FORM_LABELS[risk.type] || 'Residence';
  const propertyTypeLabel =
    risk.type === FarmRiskType.Fo4
      ? ''
      : propertyTypes.find((c) => c.id === risk.building?.propertyType)?.value || risk.building?.propertyType || '';
  const propertyTypePrefix = propertyTypeLabel ? `${propertyTypeLabel} - ` : '';
  return `${formLabel}: ${propertyTypePrefix}${risk.description}`;
};

export const getItemDisplayText = (item, farmLocations, state) => {
  if (item.type === 'location') {
    if (!item.locationId) {
      return item.description || '';
    }
    const location = (farmLocations || []).find((loc) => loc.id === item.locationId);
    return `Location: ${location?.description || ''}`;
  }

  if (!item.riskId) {
    return item.description || '';
  }

  const risk = (farmLocations || []).flatMap((loc) => loc.risks || []).find((r) => r.id === item.riskId);

  if (!risk) {
    return '';
  }

  if (item.type === 'structure') {
    return getStructureLabel(risk, state);
  }

  if (item.type === 'residence') {
    return getResidenceLabel(risk);
  }

  return '';
};

export const buildNotOnPolicyDetailFields = (item, { includeNumFamilies = true } = {}) => {
  const fields = [];

  if (item.type === 'location') {
    const formatOptions = lookupsJson.farm.legalDescriptionFormatOptions || [];
    const formatLabel =
      formatOptions.find((o) => o.id === item.legalDescription?.format)?.value ||
      item.legalDescription?.format ||
      'Not provided';
    fields.push({ name: 'legalDescriptionFormat', label: 'Legal description format', value: formatLabel, size: 6 });
    fields.push({ name: 'address', label: 'Address', value: item.addressDetails?.address || 'Not provided', size: 6 });
    fields.push({
      name: 'address2',
      label: 'Apt, unit, floor, building, etc.',
      value: item.addressDetails?.address2 || 'Not provided',
      size: 6
    });
    fields.push({ name: 'city', label: 'City', value: item.addressDetails?.city || 'Not provided', size: 6 });
    fields.push({ name: 'state', label: 'State', value: item.addressDetails?.state || 'Not provided', size: 6 });
    fields.push({ name: 'zip', label: 'Zip code', value: item.addressDetails?.zip || 'Not provided', size: 6 });

    const format = item.legalDescription?.format;
    if (format && format !== 'street') {
      if (item.addressDetails?.county) {
        fields.push({ name: 'county', label: 'County', value: item.addressDetails.county, size: 6 });
      }
      if (item.legalDescription?.section) {
        fields.push({ name: 'section', label: 'Section', value: item.legalDescription.section, size: 4 });
      }
      if (item.legalDescription?.township) {
        fields.push({ name: 'township', label: 'Township', value: item.legalDescription.township, size: 4 });
      }
      if (item.legalDescription?.range) {
        fields.push({ name: 'range', label: 'Range', value: item.legalDescription.range, size: 4 });
      }
      if (item.legalDescription?.lot) {
        fields.push({ name: 'lot', label: 'Lot', value: item.legalDescription.lot, size: 6 });
      }
      if (item.legalDescription?.block) {
        fields.push({ name: 'block', label: 'Block', value: item.legalDescription.block, size: 6 });
      }
    }
  }

  if (item.type === 'structure') {
    fields.push({
      name: 'structureDetail',
      label: 'Description of structure',
      value: item.structureDetail || 'Not provided',
      size: 12
    });
  }

  if (item.type === 'residence') {
    fields.push({ name: 'address', label: 'Address', value: item.addressDetails?.address || 'Not provided', size: 6 });
    fields.push({
      name: 'address2',
      label: 'Apt, unit, floor, building, etc.',
      value: item.addressDetails?.address2 || 'Not provided',
      size: 6
    });
    fields.push({ name: 'city', label: 'City', value: item.addressDetails?.city || 'Not provided', size: 6 });
    fields.push({ name: 'state', label: 'State', value: item.addressDetails?.state || 'Not provided', size: 6 });
    fields.push({ name: 'zip', label: 'Zip code', value: item.addressDetails?.zip || 'Not provided', size: 6 });
  }

  if (item.type !== 'structure' && includeNumFamilies) {
    fields.push({
      name: 'numFamilies',
      label: 'Number of families living',
      value: item.numFamilies ?? 'Not provided',
      size: 6
    });
  }

  return fields;
};

export const buildPropertyOptions = (farmLocations, typeConfig, state) => {
  const options = [];

  (farmLocations || []).forEach((location, locationIndex) => {
    const locationLinkId = `farmLocations[${locationIndex}]`;

    if (typeConfig.locations) {
      const locationNumFamilies =
        (location.risks || [])
          .filter((r) => RESIDENCE_RISK_TYPES.includes(r.type))
          .reduce((sum, r) => sum + (r.building?.numFamiliesInDwelling ?? 0), 0) || null;

      options.push({
        id: `location-${location.id}`,
        text: `Location: ${location.description}`,
        type: 'location',
        locationId: location.id,
        riskId: null,
        locationLinkId,
        riskLinkId: null,
        legalDescription: location.legalDescription || null,
        additionalLegalDescription: location.additionalLegalDescription || null,
        locationDirections: location.locationDirections || null,
        addressDetails: location.address || null,
        numFamilies: locationNumFamilies
      });
    }

    (location.risks || []).forEach((risk, riskIndex) => {
      let riskLinkId;
      if (risk.type === FarmRiskType.Fo6Scheduled || risk.type === FarmRiskType.Fo6Unscheduled) {
        riskLinkId = `${locationLinkId}.${risk.type}`;
      } else {
        riskLinkId = `${locationLinkId}.risks[${riskIndex}]`;
      }

      if (typeConfig.structures && risk.type === FarmRiskType.Fo6Structures) {
        options.push({
          id: `structure-${risk.id}`,
          text: getStructureLabel(risk, state),
          type: 'structure',
          locationId: location.id,
          riskId: risk.id,
          locationLinkId,
          riskLinkId,
          description: risk.description || null
        });
      }

      if (typeConfig.residences && RESIDENCE_RISK_TYPES.includes(risk.type)) {
        options.push({
          id: `residence-${risk.id}`,
          text: getResidenceLabel(risk),
          type: 'residence',
          locationId: location.id,
          riskId: risk.id,
          locationLinkId,
          riskLinkId,
          addressDetails: location.address || null,
          numFamilies: risk.building?.numFamiliesInDwelling ?? null
        });
      }
    });
  });

  options.push({
    id: 'notOnPolicy',
    text: 'Add an item not on the policy'
  });

  return options;
};

export const buildItemLinkId = (item, farmLocations) => {
  const locationIndex = (farmLocations || []).findIndex((loc) => loc.id === item.locationId);
  if (locationIndex === -1) {
    return null;
  }
  if (item.type === 'location') {
    return `farmLocations[${locationIndex}]`;
  }
  const location = farmLocations[locationIndex];
  const riskIndex = (location.risks || []).findIndex((r) => r.id === item.riskId);
  if (riskIndex === -1) {
    return null;
  }
  return `farmLocations[${locationIndex}].risks[${riskIndex}]`;
};

// --- Asset liability extension helpers ---

const ASSET_TYPE_LABELS = {
  atv: 'ATV',
  golfCart: 'Golf cart',
  unmannedAircraft: 'Unmanned aircraft',
  boat: 'Boat'
};

export const buildAssetOptions = (farmLocations, typeConfig) => {
  const options = [];

  (farmLocations || []).forEach((location) => {
    (location.risks || []).forEach((risk) => {
      (risk.coverages?.scheduleCoverages || []).forEach((coverage) => {
        if (coverage.coverageType !== 'SPP') {
          return;
        }
        (coverage.items || []).forEach((item) => {
          const assetType = item.category;
          if (!assetType || !typeConfig[assetType]) {
            return;
          }
          const typeLabel = ASSET_TYPE_LABELS[assetType] || assetType;
          options.push({
            id: item.id,
            text: `${typeLabel} - ${item.name || item.description || ''}`,
            assetType,
            locationId: location.id,
            riskId: risk.id,
            itemId: item.id,
            name: item.name,
            limit: item.limit,
            description: item.description,
            extendedDetails: item.extendedDetails || {}
          });
        });
      });
    });
  });

  options.push({
    id: 'notOnPolicy',
    text: 'Add an item not on the policy'
  });

  return options;
};

export const deriveWatercraftClassification = (classification, horsepower) => {
  if (!classification) {
    return '';
  }

  if (classification === 'misc' || classification === 'sailboat') {
    return 'Personal watercraft';
  }

  const hp = Number(horsepower) || 0;

  if (classification === 'inboard') {
    if (hp <= 200) {
      return 'Inboard and Inboard/Outboard Boats (200 hp and lower)';
    }
    return 'Inboard and Inboard/Outboard Boats (Over 200 hp)';
  }

  if (classification === 'outboard') {
    if (hp <= 100) {
      return 'Outboard Motors/Boats (100 hp and lower)';
    }
    if (hp <= 150) {
      return 'Outboard Motors/Boats (101 hp to 150 hp)';
    }
    if (hp <= 175) {
      return 'Outboard Motors/Boats (151 hp to 175 hp)';
    }
    if (hp <= 199) {
      return 'Outboard Motors/Boats (176 hp to 199 hp)';
    }
    return 'Inboard and Inboard/Outboard Boats (Over 200 hp)';
  }

  return '';
};

export const buildAssetDetailFields = (item) => {
  const fields = [];
  const ext = item.extendedDetails || {};
  const type = item.type || item.assetType;

  // Pull values from extendedDetails (on-policy SPP items) or top-level (already-saved assets)
  const year = ext.year ?? item.year;
  const make = ext.make ?? item.make;
  const model = ext.model ?? item.model;
  const serialNumber = ext.serialNumber ?? item.serialNumber;
  const description = ext.description ?? item.description;

  if (type === 'atv') {
    const cc = ext.cc ?? item.cc;
    const ccOption = lookupsJson.farm.cc?.find((o) => o.id === cc);
    fields.push({ name: 'year', label: 'Year', value: year ?? '', size: 4 });
    fields.push({ name: 'make', label: 'Make', value: make ?? '', size: 4 });
    fields.push({ name: 'model', label: 'Model', value: model ?? '', size: 4 });
    fields.push({ name: 'serialNumber', label: 'Serial number', value: serialNumber ?? '', size: 6 });
    fields.push({ name: 'cc', label: 'CC', value: ccOption?.value ?? cc ?? '', size: 6 });
    fields.push({ name: 'description', label: 'Description', value: description ?? '', size: 12 });
  }

  if (type === 'golfCart') {
    fields.push({ name: 'year', label: 'Year', value: year ?? '', size: 6 });
    fields.push({ name: 'make', label: 'Make', value: make ?? '', size: 6 });
    fields.push({ name: 'model', label: 'Model', value: model ?? '', size: 6 });
    fields.push({ name: 'serialNumber', label: 'Serial number', value: serialNumber ?? '', size: 6 });
    fields.push({ name: 'description', label: 'Description', value: description ?? '', size: 12 });
  }

  if (type === 'unmannedAircraft') {
    const weight = ext.weight ?? item.weight;
    fields.push({ name: 'year', label: 'Year', value: year ?? '', size: 6 });
    fields.push({ name: 'make', label: 'Make', value: make ?? '', size: 6 });
    fields.push({ name: 'model', label: 'Model', value: model ?? '', size: 6 });
    fields.push({ name: 'serialNumber', label: 'Serial number', value: serialNumber ?? '', size: 6 });
    fields.push({ name: 'weight', label: 'Weight (lbs)', value: weight ?? '', size: 6 });
    fields.push({ name: 'description', label: 'Description', value: description ?? '', size: 12 });
  }

  if (type === 'boat') {
    const horsepower = ext.horsepower ?? item.horsepower;
    const classification = ext.classification ?? item.classification;
    const lengthFt = ext.lengthFt ?? item.length;
    const mph = ext.milesPerHour ?? item.mph;
    const derivedClassification = deriveWatercraftClassification(classification, horsepower);
    fields.push({ name: 'classification', label: 'Classification', value: derivedClassification, size: 6 });
    fields.push({ name: 'horsepower', label: 'Horsepower', value: horsepower ?? '', size: 6 });
    fields.push({ name: 'year', label: 'Year', value: year ?? '', size: 6 });
    fields.push({ name: 'length', label: 'Length (ft)', value: lengthFt ?? '', size: 6 });
    fields.push({ name: 'mph', label: 'Miles per hour', value: mph ?? '', size: 6 });
    fields.push({ name: 'serialNumber', label: 'Serial number', value: serialNumber ?? '', size: 6 });
    fields.push({ name: 'description', label: 'Description', value: description ?? '', size: 12 });
  }

  return fields;
};

export const navigateToLocationOrRisk = (linkId) => {
  if (!linkId) {
    return;
  }

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return !!el;
  };

  if (scrollTo(linkId)) {
    return;
  }

  setTimeout(() => {
    if (scrollTo(linkId)) {
      return;
    }
    const locationId = linkId.match(/^farmLocations\[\d+\]/)?.[0];
    if (locationId && locationId !== linkId) {
      scrollTo(locationId);
    }
  }, 250);
};
